import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { X } from 'lucide-react';

export default function PrivacyNotice() {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      {/* Privacy Notice Link - Fixed footer at bottom of viewport */}
      <footer className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 py-3 z-40">
        <div className="flex justify-center">
          <button
            onClick={() => setIsOpen(true)}
            className="text-sm text-gray-500 hover:text-gray-900 underline transition-colors"
          >
            Privacy Notice
          </button>
        </div>
      </footer>

      {/* Privacy Notice Modal */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex flex-col items-center gap-2">
              <img src="/scorz-logo.png" alt="Scorz Logo" className="h-16 w-auto" />
              <DialogTitle className="text-2xl font-bold text-center">
                Privacy Notice
              </DialogTitle>
              <p className="text-sm text-gray-500">Last updated: January 2026</p>
            </div>
          </DialogHeader>

          <div className="space-y-6 text-sm">
            {/* Introduction */}
            <section>
              <p className="text-gray-700">
                Scorz is a competition management and scoring platform used to administer gymnastics competitions. Scorz is a trading name of <strong>Mullens Ltd</strong> (Company No. 17109449).
              </p>
              <p className="text-gray-700 mt-3">
                This Privacy Notice explains how personal data and related files are collected, used, stored, and protected when using the Scorz platform.
              </p>
            </section>

            <hr className="border-gray-300" />

            {/* Section 1 */}
            <section>
              <h3 className="text-lg font-semibold mb-3">1. Data Collected</h3>
              <p className="mb-3 text-gray-700">
                Scorz collects and stores personal data and competition-related files strictly for competition administration and governance purposes.
              </p>
              <p className="mb-2 font-medium">The data collected may include:</p>
              
              <div className="space-y-4 ml-4">
                <div>
                  <p className="font-medium">Gymnasts (including children):</p>
                  <ul className="list-disc ml-6 mt-1 space-y-1 text-gray-700">
                    <li>Full name</li>
                    <li>Date of birth</li>
                    <li>British Gymnastics membership number</li>
                  </ul>
                </div>

                <div>
                  <p className="font-medium">Coaches:</p>
                  <ul className="list-disc ml-6 mt-1 space-y-1 text-gray-700">
                    <li>Full name</li>
                    <li>Date of birth</li>
                    <li>Qualification details</li>
                    <li>British Gymnastics membership number</li>
                  </ul>
                </div>

                <div>
                  <p className="font-medium">Judges:</p>
                  <ul className="list-disc ml-6 mt-1 space-y-1 text-gray-700">
                    <li>Full name</li>
                    <li>Date of birth</li>
                    <li>Qualification details</li>
                    <li>British Gymnastics membership number</li>
                  </ul>
                </div>

                <div>
                  <p className="font-medium">Competition and System Data:</p>
                  <ul className="list-disc ml-6 mt-1 space-y-1 text-gray-700">
                    <li>Competition location (address)</li>
                    <li>Competition programme details, including start and finish times</li>
                    <li>Competition entry information</li>
                    <li>Gymnast floor music files (MP3 or similar formats)</li>
                    <li>Uploaded payment proof and supporting documents</li>
                    <li>Other files or data uploaded as part of competition administration</li>
                  </ul>
                </div>
              </div>
            </section>

            <hr className="border-gray-300" />

            {/* Section 2 */}
            <section>
              <h3 className="text-lg font-semibold mb-3">2. Why the Data Is Used</h3>
              <p className="mb-2 text-gray-700">Personal data and uploaded files within Scorz are used solely for:</p>
              <ul className="list-disc ml-6 space-y-1 text-gray-700">
                <li>Administration and management of gymnastics competitions</li>
                <li>Verification of eligibility, qualifications, and membership</li>
                <li>Safeguarding and governance requirements</li>
                <li>Management of competition-related materials, including gymnast-specific floor music</li>
                <li>Verification of payments and competition entries</li>
                <li>Communication with clubs in relation to competitions and events</li>
              </ul>
              <p className="mt-3 font-medium text-gray-900">Personal data is not used for marketing purposes.</p>
            </section>

            <hr className="border-gray-300" />

            {/* Section 3 */}
            <section>
              <h3 className="text-lg font-semibold mb-3">3. Lawful Basis for Processing</h3>
              <p className="mb-3 text-gray-700">
                Scorz processes personal data in order to administer gymnastics competitions safely and effectively.
              </p>
              <p className="mb-2 font-medium">The lawful basis for processing is:</p>
              <ul className="list-disc ml-6 space-y-1 text-gray-700">
                <li><strong>Legitimate interests</strong> – to run, administer, and manage competitions properly</li>
                <li><strong>Legal and governance obligations</strong> – including safeguarding, eligibility checking, and compliance with British Gymnastics and regional requirements</li>
              </ul>
              <p className="mt-3 text-gray-700">Only the data necessary for these purposes is collected and processed.</p>
            </section>

            <hr className="border-gray-300" />

            {/* Section 4 */}
            <section>
              <h3 className="text-lg font-semibold mb-3">4. Data Controller and Responsibilities</h3>
              <p className="mb-2 text-gray-700">For the purposes of data protection law:</p>
              <ul className="list-disc ml-6 space-y-1 mb-4 text-gray-700">
                <li><strong>Mullens Ltd</strong> (Company No. 17109449), trading as Scorz, is the Data Controller for the Scorz platform.</li>
                <li>Freddie Mullen, acting on behalf of Mullens Ltd, provides system support and assistance relating to user accounts and technical issues.</li>
              </ul>
              
              <p className="font-medium mb-2">Role of Clubs</p>
              <p className="mb-2 text-gray-700">Clubs act as data providers within the system.</p>
              <p className="mb-2 text-gray-700">Clubs are responsible for:</p>
              <ul className="list-disc ml-6 space-y-1 text-gray-700">
                <li>Ensuring the accuracy of the data they upload</li>
                <li>Keeping gymnast, coach, and judge information up to date</li>
                <li>Removing individuals they no longer have a legitimate reason to retain within the system</li>
              </ul>
            </section>

            <hr className="border-gray-300" />

            {/* Section 5 */}
            <section>
              <h3 className="text-lg font-semibold mb-3">5. Platform Hosting, Storage, and Security</h3>
              <p className="mb-3 text-gray-700">
                All data within the Scorz platform is hosted and stored using <strong>Cloudflare</strong>.
              </p>
              <p className="mb-2 text-gray-700">This includes, but is not limited to:</p>
              <ul className="list-disc ml-6 space-y-1 mb-4 text-gray-700">
                <li>Personal data records</li>
                <li>Competition entry information</li>
                <li>Gymnast floor music files (MP3s)</li>
                <li>Uploaded payment proof and supporting documents</li>
                <li>Any other files or data uploaded to the system</li>
              </ul>
              <p className="mb-3 text-gray-700">
                Cloudflare provides both hosting infrastructure and security services, including encrypted data transmission, protection against unauthorised access, malicious activity, and network attacks.
              </p>
              <p className="mb-3 text-gray-700">
                Cloudflare acts as a data processor on behalf of Mullens Ltd and processes data only in accordance with the Data Controller's instructions and appropriate contractual and security safeguards.
              </p>
              <p className="text-gray-700">
                Appropriate technical and organisational measures are in place to protect all stored data and ensure system reliability and security.
              </p>
            </section>

            <hr className="border-gray-300" />

            {/* Section 6 */}
            <section>
              <h3 className="text-lg font-semibold mb-3">6. System Monitoring and Security Logging</h3>
              <p className="mb-2 text-gray-700">Scorz uses technical monitoring and security tools to:</p>
              <ul className="list-disc ml-6 space-y-1 mb-3 text-gray-700">
                <li>Maintain platform security</li>
                <li>Ensure performance and reliability</li>
                <li>Diagnose and resolve technical issues</li>
              </ul>
              <p className="mb-3 text-gray-700">
                These tools are used solely for security and performance purposes and are not used for marketing or profiling.
              </p>
              <p className="text-gray-700">
                Where possible, personal data entered into forms is masked or not recorded by monitoring tools.
              </p>
            </section>

            <hr className="border-gray-300" />

            {/* Section 7 */}
            <section>
              <h3 className="text-lg font-semibold mb-3">7. Data Retention and Deletion</h3>
              <p className="mb-2 text-gray-700">Personal data and uploaded files are retained only for as long as necessary to support:</p>
              <ul className="list-disc ml-6 space-y-1 mb-3 text-gray-700">
                <li>Competition administration</li>
                <li>Competition history and records</li>
              </ul>
              <p className="mb-3 text-gray-700">Data that is no longer required will be deleted.</p>
              <p className="mb-2 text-gray-700">Clubs are responsible for:</p>
              <ul className="list-disc ml-6 space-y-1 mb-3 text-gray-700">
                <li>Removing gymnasts, coaches, or judges they no longer represent</li>
                <li>Keeping their account data accurate and up to date</li>
              </ul>
              <p className="text-gray-700">
                Requests to delete an entire club account must be made using the contact details below.
              </p>
            </section>

            <hr className="border-gray-300" />

            {/* Section 8 */}
            <section>
              <h3 className="text-lg font-semibold mb-3">8. Rights and Access</h3>
              
              <p className="font-medium mb-2">Club Access</p>
              <p className="mb-2 text-gray-700">Clubs can:</p>
              <ul className="list-disc ml-6 space-y-1 mb-4 text-gray-700">
                <li>Access their data at any time via their secure Scorz portal</li>
                <li>View, edit, and update personal data and uploaded files</li>
              </ul>
              
              <p className="font-medium mb-2">Individual Rights</p>
              <p className="mb-2 text-gray-700">Individuals have rights under data protection law, including the right to:</p>
              <ul className="list-disc ml-6 space-y-1 mb-3 text-gray-700">
                <li>Access their personal data</li>
                <li>Request correction of inaccurate data</li>
                <li>Request deletion of their personal data where appropriate</li>
              </ul>
              <p className="text-gray-700">
                Requests should be made via the relevant club or by contacting Scorz directly.
              </p>
            </section>

            <hr className="border-gray-300" />

            {/* Section 9 */}
            <section>
              <h3 className="text-lg font-semibold mb-3">9. Data Protection Impact Assessment (DPIA)</h3>
              <p className="text-gray-700">
                Because Scorz stores and processes children's data in a central system, Mullens Ltd has assessed the associated data protection risks and has carried out a <strong>Data Protection Impact Assessment (DPIA)</strong> to ensure appropriate safeguards are in place.
              </p>
            </section>

            <hr className="border-gray-300" />

            {/* Section 10 */}
            <section>
              <h3 className="text-lg font-semibold mb-3">10. Contact Details</h3>
              <p className="mb-3 text-gray-700">
                For questions relating to data protection, account management, or this Privacy Notice, please contact:
              </p>
              <p className="text-lg font-medium">
                <a href="mailto:support@scorz.co.uk" className="text-black hover:underline">support@scorz.co.uk</a>
              </p>
            </section>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
